// The one start dialog's parts (features/startwork/StartWorkDialog.tsx
// holds the state and Who): When (now, once, a timetable, every N, or
// after another workflow's run: a workflow only, ./chainModel.ts), What
// (the brief, the reminder line or a workflow's settings) and Report (in
// chat or by email, from an agent; or quiet). Rules and words:
// ./scheduleFormModel.ts and features/startwork/startWorkModel.ts.

import type { ReactNode } from "react";

import { Checkbox } from "@/components/ui/Checkbox";
import { Dropdown, type DropdownOption } from "@/components/ui/Dropdown";
import { InfoTip } from "@/components/ui/InfoTip";
import { Input, Textarea } from "@/components/ui/Input";
import { Segmented } from "@/components/ui/Segmented";
import { TokenInput } from "@/components/ui/TokenInput";
import { Field } from "@/features/settings/ui";
import type { StartWhen, WhenOption } from "@/features/startwork/startWorkModel";
import { MAX_TITLE } from "@/features/tasks/taskModel";
import { browserZone, zoneLabel } from "@/features/workflows/editor/scheduleModel";

import { CronFields } from "../CronFields";
import { CHAIN_FIRES_PER_DAY, UPSTREAM_PLACEHOLDERS } from "./chainModel";
import { everyMinutes, floorMinutes, minutesLimit, reportsBack, type ChannelChoice, type EveryUnit, type FormField, type ReportChoice, type ScheduleForm } from "./scheduleFormModel";
import { everyWords } from "./scheduleWords";
import { WorkflowParamsFields } from "./WorkflowParamsFields";

type Set = (patch: Partial<ScheduleForm>) => void;
type Problems = Partial<Record<FormField, string>>;

/** A part of the form: a label over its controls, as a field has. */
export function Part({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-fg">{title}</h3>
      {children}
    </section>
  );
}

// ─── when ───

const UNIT_OPTIONS: DropdownOption<EveryUnit>[] = [
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

interface WhenProps {
  form: ScheduleForm;
  set: Set;
  shown: Problems;
  tried: boolean;
  /** The saved workflows, as a chain may follow one (the form's own workflow left out). */
  workflows: DropdownOption<string>[];
  /** The choices the dialog offers (startWorkModel.whenChoices); one or none hides the row. */
  choices: WhenOption[];
  when: StartWhen;
  onWhen: (when: StartWhen) => void;
}

export function WhenFields({ form, set, shown, tried, workflows, choices, when, onWhen }: WhenProps) {
  const floor = floorMinutes(form);
  const zone = browserZone();
  if (choices.length < 2 && when === "now") return null;
  return (
    <Part title="When">
      {choices.length > 1 && <Segmented<StartWhen> ariaLabel="When" size="sm" value={when} options={choices} onChange={onWhen} />}
      {when === "cron" && <CronFields cron={form.cron} tz={form.tz} floor={floor} showProblem={tried} onChange={(p) => set(p)} />}
      {when === "every" && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-fg">
          <span>Every</span>
          <Input aria-label="How many" type="number" inputMode="numeric" min={1} className="w-20" value={Number.isFinite(form.every) ? form.every : ""} onChange={(e) => set({ every: e.target.valueAsNumber })} />
          <Dropdown<EveryUnit> ariaLabel="Unit" size="sm" minWidth={110} matchTriggerWidth={false} value={form.everyUnit} options={UNIT_OPTIONS} onChange={(everyUnit) => set({ everyUnit })} />
        </div>
      )}
      {when === "at" && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-fg">
          <Input aria-label="Day and time" type="datetime-local" className="w-[220px]" value={form.at} onChange={(e) => set({ at: e.target.value })} />
          {zone && <span className="text-xs text-fg-muted">your time ({zoneLabel(zone)})</span>}
        </div>
      )}
      {when === "after" && <AfterFields form={form} set={set} workflows={workflows} />}
      {when !== "cron" && when !== "now" && shown.when && <p className="text-xs text-danger">{shown.when}</p>}
      {when === "every" && !shown.when && Number.isFinite(form.every) && form.every > 0 && <p className="text-xs text-fg-muted">{everyWords(everyMinutes(form))}</p>}
      {form.who === "message" && (when === "cron" || when === "every") && <Checkbox checked={form.highFrequency} onChange={(highFrequency) => set({ highFrequency })} label="More often than every 15 minutes" />}
    </Part>
  );
}

const AFTER_NOTE = [
  `Once for each run, at most ${CHAIN_FIRES_PER_DAY} times a day, never after a run that was stopped or failed as a whole. Runs that ended before this schedule was saved are not followed; when several end close together, only the latest is.`,
  `In a text setting, ${UPSTREAM_PLACEHOLDERS.map((p) => `${p.token} is replaced by ${p.means}`).join(" and ")}.`,
].join("\n");

/** After another workflow: which one, and whether a run with failed steps counts. */
function AfterFields({ form, set, workflows }: { form: ScheduleForm; set: Set; workflows: DropdownOption<string>[] }) {
  const options = workflows.filter((w) => w.value !== form.ref);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm text-fg">
        <span className="inline-flex items-center gap-1.5">
          After a run of
          <InfoTip text={AFTER_NOTE} />
        </span>
        {/* the workflow and its verb wrap together, so "succeeds" never sits on a line alone */}
        <span className="inline-flex items-center gap-2">
          <Dropdown ariaLabel="The workflow it runs after" size="sm" minWidth={200} matchTriggerWidth={false} value={form.afterWorkflow} options={options} placeholder="Choose a workflow" onChange={(afterWorkflow) => set({ afterWorkflow })} />
          <span>{form.afterOn === "completed_or_failures" ? "completes" : "succeeds"}</span>
        </span>
      </div>
      <Checkbox checked={form.afterOn === "completed_or_failures"} onChange={(on) => set({ afterOn: on ? "completed_or_failures" : "completed" })} label="Also when it completes with failed steps" />
    </div>
  );
}

// ─── what ───

interface WhatProps {
  customerId: string;
  form: ScheduleForm;
  set: Set;
  shown: Problems;
  tried: boolean;
  /** A schedule (not a start now): its Title then names each task it gives. */
  later?: boolean;
  /** The most the chosen worker may run (a subagent's own limit), when known. */
  maxMinutes?: number | null;
  /** Told whether a workflow's settings form flags a value, after each change (each field says its own). */
  onParamsProblem?: (bad: boolean) => void;
  /** Only the words (Give a task opened on a named worker: the rest is behind its Change). */
  briefOnly?: boolean;
  /** The worker's browser is off: nothing to ask for. */
  noBrowser?: boolean;
}

export function WhatFields({ customerId, form, set, shown, tried, later = false, maxMinutes = null, onParamsProblem, briefOnly = false, noBrowser = false }: WhatProps) {
  if (form.who === "message") {
    return (
      <Field label="The reminder" error={shown.brief}>
        {({ id, describedBy }) => <Textarea id={id} aria-describedby={describedBy} data-autofocus value={form.brief} placeholder="Call Emma about the lease." onChange={(e) => set({ brief: e.target.value })} />}
      </Field>
    );
  }
  if (form.who === "workflow") {
    return (
      <WorkflowParamsFields
        customerId={customerId}
        slug={form.ref}
        params={form.params}
        showProblems={tried}
        onChange={(params, bad) => {
          set({ params });
          onParamsProblem?.(bad);
        }}
      />
    );
  }
  // the time limit and the browser belong to an agent's or a subagent's task: not shown before one is chosen
  const chosen = form.who !== "agent" || !!form.ref;
  const rule = chosen ? minutesLimit(form) : null;
  const limit = rule && maxMinutes && form.who !== "team" ? { ...rule, max: Math.min(rule.max, maxMinutes) } : rule;
  return (
    <>
      <Field label={form.question ? "The question" : "What to do"} error={shown.brief}>
        {({ id, describedBy }) => (
          <Textarea id={id} aria-describedby={describedBy} data-autofocus fieldSize="lg" placeholder={form.question ? undefined : "The goal, where to look, what to hand back."} value={form.brief} onChange={(e) => set({ brief: e.target.value })} />
        )}
      </Field>
      {!briefOnly && (
        <>
      <Field
        label={
          later ? (
            <span className="inline-flex items-center gap-1.5">
              Title
              <InfoTip text="What each task it gives is called; else the schedule's name." />
            </span>
          ) : (
            "Title"
          )
        }
        error={shown.title}
      >
        {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} maxLength={MAX_TITLE + 20} placeholder="Optional" value={form.title} onChange={(e) => set({ title: e.target.value })} />}
      </Field>
      {limit && (
        <Field label="Time limit" error={shown.minutes}>
          {({ id, describedBy }) => (
            <div className="flex items-center gap-2 text-sm text-fg-muted">
              <Input id={id} aria-describedby={describedBy} className="w-28" type="number" inputMode="numeric" min={limit.min} max={limit.max} value={Number.isFinite(form.minutes) ? form.minutes : ""} onChange={(e) => set({ minutes: e.target.valueAsNumber })} />
              min
            </div>
          )}
        </Field>
      )}
      {chosen && form.who !== "team" && !noBrowser && <Checkbox checked={form.needsBrowser} onChange={(needsBrowser) => set({ needsBrowser })} label="Needs the browser" />}
        </>
      )}
    </>
  );
}

// ─── report ───

const REPORT_OPTIONS: { value: ReportChoice; label: string }[] = [
  { value: "chat", label: "In chat" },
  { value: "email", label: "By email" },
  { value: "quiet", label: "Quiet" },
];
const CHANNEL_LABEL: Readonly<Record<ChannelChoice, string>> = { auto: "Where you last talked", telegram: "Telegram", whatsapp: "WhatsApp" };

interface ReportProps {
  form: ScheduleForm;
  set: Set;
  shown: Problems;
  /** The operator's ids per channel for the reporting agent (the list's `recipients[from]`). */
  recipients: Record<string, string[]>;
  /** The reporting agent ("From") and the standard agents it may be. */
  from: string;
  agents: DropdownOption<string>[];
  onFrom: (from: string) => void;
  /** false on Corvus storage: nothing is emailed out, so no "By email" (one already chosen stays to be changed). */
  emailOut?: boolean;
}

/** How you hear back: in chat or by email, from an agent; or quiet. Nothing for the Team or a workflow (their own end-of-run settings). */
export function ReportFields({ form, set, shown, recipients, from, agents, onFrom, emailOut = true }: ReportProps) {
  if (!reportsBack(form.who)) return null;
  const mode: ReportChoice = form.who === "message" ? "chat" : form.report;
  const reportOptions = emailOut || mode === "email" ? REPORT_OPTIONS : REPORT_OPTIONS.filter((o) => o.value !== "email");
  const channels: DropdownOption<ChannelChoice>[] = (["auto", "telegram", "whatsapp"] as const)
    .filter((c) => c === "auto" || (recipients[c] ?? []).length > 0 || form.channel === c)
    .map((c) => ({ value: c, label: CHANNEL_LABEL[c] }));
  const ids = form.channel === "auto" ? [] : recipients[form.channel] ?? [];
  const toOptions: DropdownOption<string>[] = [{ value: "", label: "Your first id there" }, ...ids.map((v) => ({ value: v, label: v }))];
  if (form.to && !ids.includes(form.to)) toOptions.push({ value: form.to, label: `${form.to} (not one of your ids)` });
  const fromPicker = (
    <>
      <span className="inline-flex items-center gap-1.5">
        From
        <InfoTip text={form.who === "message" ? "The reminder comes to you in this agent's chat." : mode === "email" ? "Sent from this agent's Gmail." : "Its answer comes to you in this agent's chat."} />
      </span>
      <Dropdown ariaLabel="From" size="sm" minWidth={150} matchTriggerWidth={false} value={from} options={agents} placeholder="Choose an agent" onChange={onFrom} />
    </>
  );
  return (
    <Part title="Report">
      {form.who !== "message" && <Segmented<ReportChoice> ariaLabel="Report" size="sm" value={mode} options={reportOptions} onChange={(report) => set({ report })} />}
      {mode === "chat" && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-fg">
          {fromPicker}
          <Dropdown<ChannelChoice> ariaLabel="Channel" size="sm" minWidth={170} matchTriggerWidth={false} value={form.channel} options={channels} onChange={(channel) => set({ channel, to: "" })} />
          {form.channel !== "auto" && <Dropdown ariaLabel="Your id" size="sm" minWidth={170} matchTriggerWidth={false} value={form.to} options={toOptions} onChange={(to) => set({ to })} />}
        </div>
      )}
      {mode === "email" && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm text-fg">{fromPicker}</div>
          <Field
            label={
              <span className="inline-flex items-center gap-1.5">
                To
                <InfoTip text="You, or addresses on the mail allowlist." />
              </span>
            }
            error={shown.email}
          >
            {() => <TokenInput ariaLabel="Email addresses" values={form.emailTo} onChange={(emailTo) => set({ emailTo })} placeholder="name@company.com" validate={(v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) || "Not an email address"} />}
          </Field>
        </>
      )}
      {mode !== "quiet" && shown.owner && <p className="text-xs text-danger">{shown.owner}</p>}
    </Part>
  );
}
